import React from 'react';
import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  useTheme,
  Skeleton,
  Box
} from '@mui/material';
import '../Css/Layout/LayoutConstants.css';

type TableComponentProps = {
  value: any[] | Record<string, any>;
  title?: string;
  noCard?: boolean;
  titleColor?: string;
  fixedHeight?: number;
  loading?: boolean;
};

const TableComponent: React.FC<TableComponentProps> = ({ value, title, noCard = false, titleColor, fixedHeight, loading = false }) => {
  const theme = useTheme();

  const rows = Array.isArray(value) ? value : [value];
  const keys = Object.keys(rows[0] || {});
  const isEmpty =
    !value ||
    (Array.isArray(value) && value.length === 0) ||
    (!Array.isArray(value) && typeof value !== 'object');

  // Número estimado de colunas baseado nos dados ou padrão
  const estimatedColumns = Math.max(keys.length || 3, 3);
  const estimatedRows = 5; // Número de linhas skeleton a mostrar

  const renderSkeletonTable = () => (
    <TableContainer
      component={Paper}
      sx={{

        overflowX: 'auto',
        bgcolor: 'transparent',

        ...(fixedHeight && {
          height: fixedHeight,
          maxHeight: fixedHeight,
          overflow: 'auto'
        })
      }}
    >
      {title && !noCard && (
        <Typography
          variant="subtitle1"
          align="center"
          sx={{
            color: titleColor || theme.palette.text.secondary,
            padding: 'var(--spacing-md)',
            bgcolor: theme.palette.background.paper,

          }}
        >
          {title}
        </Typography>
      )}
      <Table size="small" stickyHeader>
        <TableHead sx={{ bgcolor: theme.palette.background.paper }}>
          {title && noCard && (
            <TableRow>
              <TableCell
                colSpan={estimatedColumns + 1}
                sx={{
                  bgcolor: theme.palette.background.paper,
                  borderLeft: 'none',
                  borderRight: 'none',
                  borderBottom: `1px solid ${theme.palette.divider}`,
                  position: 'sticky',
                  top: 0,
                  zIndex: 100,
                  padding: 'var(--spacing-md)'
                }}
              >
                <Typography
                  variant="subtitle1"
                  align="center"
                  sx={{ color: theme.palette.text.secondary }}
                >
                  {title}
                </Typography>
              </TableCell>
            </TableRow>
          )}
          <TableRow>
            <TableCell
              sx={{
                fontWeight: 600,
                color: theme.palette.text.primary,
                textTransform: 'uppercase',
                bgcolor: theme.palette.grey[300],
                borderLeft: 'none',
                borderRight: 'none',
                width: '50px',
                textAlign: 'center',
                position: 'sticky',
                top: title && noCard ? '57px' : 0,
                zIndex: 100,
                padding: 'var(--spacing-md) var(--spacing-sm)'
              }}
            >
              #
            </TableCell>
            {Array.from({ length: estimatedColumns }).map((_, index) => (
              <TableCell
                key={index}
                sx={{
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                  textTransform: 'capitalize',
                  bgcolor: theme.palette.grey[300],
                  borderLeft: 'none',
                  borderRight: 'none',
                  position: 'sticky',
                  top: title && noCard ? '57px' : 0,
                  zIndex: 100,
                  padding: 'var(--spacing-md) var(--spacing-sm)'
                }}
              >
                <Skeleton variant="text" width="80%" height={20} />
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {Array.from({ length: estimatedRows }).map((_, rowIndex) => (
            <TableRow
              key={rowIndex}
              sx={{
                backgroundColor:
                  rowIndex % 2 === 0
                    ? theme.palette.background.paper
                    : theme.palette.action.hover
              }}
            >
              <TableCell
                sx={{
                  borderLeft: 'none',
                  borderRight: 'none',
                  textAlign: 'center',
                  width: '50px',
                  color: theme.palette.text.secondary,
                  fontWeight: 500,
                  padding: 'var(--spacing-md) var(--spacing-sm)'
                }}
              >
                <Skeleton variant="text" width="100%" height={16} />
              </TableCell>
              {Array.from({ length: estimatedColumns }).map((_, cellIndex) => (
                <TableCell
                  key={cellIndex}
                  sx={{
                    borderLeft: 'none',
                    borderRight: 'none',
                    padding: 'var(--spacing-md) var(--spacing-sm)'
                  }}
                >
                  <Skeleton variant="text" width="70%" height={16} />
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const renderTable = () => (
    <TableContainer
      component={Paper}
      sx={{

        overflowX: 'auto',
        bgcolor: 'transparent',

        ...(fixedHeight && {
          height: fixedHeight,
          maxHeight: fixedHeight,
          overflow: 'auto'
        })
      }}
    >
      {title && !noCard && (
        <Typography
          variant="subtitle1"
          align="center"
          sx={{
            color: titleColor || theme.palette.text.secondary,
            padding: 'var(--spacing-md)',
            bgcolor: theme.palette.background.paper,

          }}
        >
          {title}
        </Typography>
      )}
      <Table size="small" stickyHeader>
        <TableHead sx={{ bgcolor: theme.palette.background.paper }}>
          {title && noCard && (
            <TableRow>
              <TableCell
                colSpan={keys.length + 1}
                sx={{
                  bgcolor: theme.palette.background.paper,
                  borderLeft: 'none',
                  borderRight: 'none',
                  borderBottom: `1px solid ${theme.palette.divider}`,
                  position: 'sticky',
                  top: 0,
                  zIndex: 100,
                  padding: 'var(--spacing-md)'
                }}
              >
                <Typography
                  variant="subtitle1"
                  align="center"
                  sx={{ color: theme.palette.text.secondary }}
                >
                  {title}
                </Typography>
              </TableCell>
            </TableRow>
          )}
          <TableRow>
            {isEmpty && !loading ? (
              <TableCell
                colSpan={keys.length + 1}
                sx={{
                  bgcolor: theme.palette.grey[300],
                  borderLeft: 'none',
                  borderRight: 'none',
                  position: 'sticky',
                  top: title && noCard ? '57px' : 0,
                  zIndex: 100,
                  padding: 'var(--spacing-md)'
                }}
              >
                <Typography variant="body2" align="center">
                  Nenhum dado disponível.
                </Typography>
              </TableCell>
            ) : (
              <>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    color: theme.palette.text.primary,
                    textTransform: 'uppercase',
                    bgcolor: theme.palette.grey[300],
                    borderLeft: 'none',
                    borderRight: 'none',
                    width: '50px',
                    textAlign: 'center',
                    position: 'sticky',
                    top: title && noCard ? '57px' : 0,
                    zIndex: 100,
                    padding: 'var(--spacing-md) var(--spacing-sm)'
                  }}
                >
                  #
                </TableCell>
                {keys.map((key) => (
                  <TableCell
                    key={key}
                    sx={{
                      fontWeight: 600,
                      color: theme.palette.text.primary,
                      textTransform: 'capitalize',
                      bgcolor: theme.palette.grey[300],
                      borderLeft: 'none',
                      borderRight: 'none',
                      position: 'sticky',
                      top: title && noCard ? '57px' : 0,
                      zIndex: 100,
                      padding: 'var(--spacing-md) var(--spacing-sm)'
                    }}
                  >
                    {key}
                  </TableCell>
                ))}
              </>
            )}
          </TableRow>
        </TableHead>
        {!isEmpty && !loading && (
          <TableBody>
            {rows.map((row, rowIndex) => (
              <TableRow
                key={rowIndex}
                sx={{
                  backgroundColor:
                    rowIndex % 2 === 0
                      ? theme.palette.background.paper
                      : theme.palette.action.hover
                }}
              >
                <TableCell
                  sx={{
                    borderLeft: 'none',
                    borderRight: 'none',
                    textAlign: 'center',
                    width: '50px',
                    color: theme.palette.text.secondary,
                    fontWeight: 500,
                    padding: 'var(--spacing-md) var(--spacing-sm)'
                  }}
                >
                  {rowIndex + 1}
                </TableCell>
                {keys.map((key) => (
                  <TableCell
                    key={key}
                    sx={{
                      borderLeft: 'none',
                      borderRight: 'none',
                      padding: 'var(--spacing-md) var(--spacing-sm)'
                    }}
                  >
                    {typeof row[key] === 'object' && row[key] !== null
                      ? JSON.stringify(row[key])
                      : String(row[key])}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        )}
      </Table>
    </TableContainer>
  );

  if (noCard) {
    return (
      <Grid item xs={12} sx={{ width: '100%' }} className="table-wrapper">
        {loading ? renderSkeletonTable() : renderTable()}
      </Grid>
    );
  }

  return (
    <Grid item xs={12} className="table-wrapper">
      <Card
        sx={{
  
  
          bgcolor: theme.palette.background.paper,
          '&:hover': {
            boxShadow: 'var(--shadow-card-hover)',
          }
        }}
      >
        <CardContent sx={{ padding: 'var(--spacing-md)' }}>
          {loading ? renderSkeletonTable() : renderTable()}
        </CardContent>
      </Card>
    </Grid>
  );
};

export default TableComponent;
